import { put, call, select } from "redux-saga/effects";
import { Map } from "immutable";
import {
  fetchKiosks,
  fetchKiosk,
  updateKiosk,
  publishKioskProject,
  revertKioskBundle,
  deleteKiosk,
  updatePublishHistoryEntry,
  fetchKioskAppVersions
} from "api";
import * as CONST from "actions/action_constants";
import * as selectors from "./selectors";

export function* fetchKiosksSaga(action) {
  try {
    const { response, error } = yield call(fetchKiosks, action.username);

    if (response) {
      const { list } = response;
      yield put({
        type: CONST.KIOSKS_FETCH_SUCCESS,
        list
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.KIOSKS_FETCH_ERROR, message });
  }
}

export function* fetchKioskSaga(action) {
  try {
    const { kioskID } = action;
    const { response, error } = yield call(fetchKiosk, kioskID, action.filters);

    if (response) {
      yield put({
        type: CONST.KIOSK_FETCH_SUCCESS,
        kioskID,
        kioskData: response
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.KIOSK_FETCH_ERROR, message });
  }
}

export function* updateKioskSaga(action) {
  try {
    const { kioskID } = action;
    const { saveType } = action;

    const kiosk = yield select(
      selectors.getKiosk,
      kioskID
    );
    const { response, error } = yield call(
      updateKiosk,
      kioskID,
      kiosk,
      saveType
    );

    if (response) {
      yield put({
        type: CONST.KIOSK_UPDATE_SUCCESS,
        kioskData: response
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.KIOSK_UPDATE_ERROR, message });
  }
}

export function* publishKioskProjectSaga(action) {
  const { kioskID } = action;
  try {
    const kiosk = yield select(selectors.getKiosk, kioskID);
    if (!kiosk || !kiosk.projectID) {
      throw new Error("Kiosk not found or has no associated project");
    }

    const project = yield select(selectors.getProject, kiosk.projectID);
    if (!project) {
      throw new Error("Project not found");
    }

    // Get project types to determine if this is a story-based or collection-based project
    const projectTypes = yield select(selectors.getProjectTypes) || {};
    
    // Determine project type: if project.typeID matches story type ID, it's "story", otherwise "collection"
    const projectType = project.typeID && projectTypes.story && project.typeID === projectTypes.story._id
      ? "story"
      : "collection";

    /*
    Send the selected projectID in the request body. The publish
    endpoint uses this as the source of truth for which project to
    bundle and persists it to kiosk.projectID as part of the publish
    flow. The kiosk's stored projectID is no longer updated client-side
    on dropdown change -- it only changes when the user actually
    publishes (consistent with the revert flow).
    */
    const { response, error } = yield call(
      publishKioskProject,
      kioskID,
      projectType,
      { projectID: kiosk.projectID }
    );

    if (response) {
      yield put({
        type: CONST.KIOSK_PUBLISH_SUCCESS,
        projectData: response
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    /*
    Include kioskID so the kiosks reducer can attribute the error to a
    specific kiosk. PublishManager observes this per-kiosk error to
    release the "Acquiring details..." gate when the publish POST
    never produced a "preparing" history entry.
    */
    yield put({ type: CONST.KIOSK_PUBLISH_ERROR, kioskID, message });
  }
}

export function* revertKioskSaga(action) {
  try {
    const { kioskID } = action;
    const { bundleID } = action;

    const { response, error } = yield call(
      revertKioskBundle,
      kioskID,
      bundleID
    );

    if (response) {
      yield put({
        type: CONST.KIOSK_REVERT_SUCCESS,
        kioskID
      });
      yield put({
        type: CONST.KIOSK_FETCH_REQUEST,
        kioskID,
        filters: null
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.KIOSK_REVERT_ERROR, message });
  }
}

export function* deleteKioskSaga(action) {
  try {
    const kioskID = action.kioskID || action.selected;

    const { response, error } = yield call(deleteKiosk, kioskID);

    if (response) {
      yield put({ type: CONST.KIOSK_DELETE_SUCCESS, kioskID });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.KIOSK_DELETE_ERROR, message });
  }
}

export function* updatePublishHistoryEntrySaga(action) {
  try {
    const { kioskID } = action;
    const { publishID, stage, status, metadata } = action;

    const { response, error } = yield call(
      updatePublishHistoryEntry,
      kioskID,
      publishID,
      stage,
      status,
      metadata
    );

    if (response) {
      yield put({
        type: CONST.KIOSK_FETCH_REQUEST,
        kioskID,
        filters: null
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.KIOSK_UPDATE_ERROR, message });
  }
}

export function* fetchKioskAppVersionsSaga() {
  try {
    const { response, error } = yield call(fetchKioskAppVersions);

    if (response) {
      yield put({
        type: CONST.KIOSK_APP_VERSIONS_FETCH_SUCCESS,
        versions: response
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.KIOSK_APP_VERSIONS_FETCH_ERROR, message });
  }
}
